import {
  Module,
  VuexModule,
  getModule,
  Mutation,
} from "vuex-module-decorators";

import store from "@/store";
import {
  BuildIdentity,
  getBuildIdentityKey,
} from "@/utils/BuildInfo";

const name = "appUpdate";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
if ((store.state as any)[name]) {
  store.unregisterModule(name);
}

/**
 * State of the "a new version is available" offer.
 *
 * Everything the banner shows is derived from this state, never from a timer: a held offer
 * reappears on its own as soon as the last hold is released, and a build that arrives after a
 * dismissal resurfaces because the dismissal is remembered per build, not as a boolean.
 *
 * Dismissal deliberately lives in memory only. It must last for the session and no longer —
 * persisting it would silence the rest of the session's deploys and leave people on an old build.
 */
@Module({ dynamic: true, namespaced: true, store, name })
export class AppUpdateVuexModule extends VuexModule {
  /** The deployed build that differs from the running one, once one has been seen. */
  pendingBuild: BuildIdentity | null = null;

  /** Key of the build the user waved away, from {@link getBuildIdentityKey}. */
  dismissedBuildKey: string | null = null;

  /**
   * Ids of the reasons currently holding the offer back — unsaved edits, an export in flight, an
   * open edit dialog. Registered through `@/utils/UpdateHold`.
   */
  holdIds: string[] = [];

  /** True while the reload is being applied, so the primary button can show a busy state. */
  isApplyingUpdate = false;

  /**
   * Whether the banner should be on screen right now.
   *
   * Reads state only (no getter chaining), because `vuex-module-decorators` does not guarantee
   * getter-to-getter access.
   */
  get isUpdateOffered(): boolean {
    const pending = this.pendingBuild;

    if (pending === null) {
      return false;
    }

    if (getBuildIdentityKey(pending) === this.dismissedBuildKey) {
      return false;
    }

    return this.holdIds.length === 0;
  }

  /** True when a newer deploy is known, whether or not it is currently being offered. */
  get hasPendingUpdate(): boolean {
    return this.pendingBuild !== null;
  }

  @Mutation
  setPendingBuild(build: BuildIdentity) {
    this.pendingBuild = build;
  }

  /**
   * Hides the offer for the pending build only. A different build seen afterwards no longer
   * matches the remembered key, so the banner comes back.
   */
  @Mutation
  dismissPendingUpdate() {
    this.dismissedBuildKey =
      this.pendingBuild === null ? null : getBuildIdentityKey(this.pendingBuild);
  }

  @Mutation
  addHold(id: string) {
    if (this.holdIds.includes(id)) {
      return;
    }

    this.holdIds = [...this.holdIds, id];
  }

  @Mutation
  removeHold(id: string) {
    this.holdIds = this.holdIds.filter((holdId) => holdId !== id);
  }

  @Mutation
  setApplyingUpdate(isApplying: boolean) {
    this.isApplyingUpdate = isApplying;
  }

  /** Resets everything; used by tests and when the authenticated shell is torn down. */
  @Mutation
  clear() {
    this.pendingBuild = null;
    this.dismissedBuildKey = null;
    this.holdIds = [];
    this.isApplyingUpdate = false;
  }
}

export const AppUpdateModule = getModule(AppUpdateVuexModule);
